import { Box, HStack, IconButton, Input, Text } from '@chakra-ui/react';
import { Lock, Xmark } from 'iconoir-react';
import { useTranslation } from 'react-i18next';
import type { UtmKeyError } from './utils';

const KEY_COLUMN_WIDTH = '140px';

interface UtmParameterRowProps {
  index: number;
  utmKey: string;
  value: string;
  // Non-null when the key already exists in the destination URL: the row is
  // locked, read-only, and shows the value carried by the URL.
  lockedValue: string | null;
  // Whether the key input is read-only (a convention key at its canonical
  // position). Derived from origin, not the live text, so typing a convention
  // key into a custom row never turns its input read-only mid-edit.
  keyReadOnly: boolean;
  // Set for an editable key that is invalid or duplicated (inline error).
  keyError: UtmKeyError;
  onValueChange: (index: number, value: string) => void;
  onKeyChange: (index: number, key: string) => void;
  onRemove: (index: number) => void;
}

/**
 * One editable UTM key/value row. Three shapes: a locked row (key already in the
 * URL, cadenas + "already in URL" tag, read-only), a convention row (read-only
 * key, editable value), and a custom row (editable key + value, removable). A
 * custom row with an invalid or duplicate key shows an inline error; such pairs
 * are excluded from the saved payload.
 */
export function UtmParameterRow({
  index,
  utmKey,
  value,
  lockedValue,
  keyReadOnly,
  keyError,
  onValueChange,
  onKeyChange,
  onRemove,
}: UtmParameterRowProps) {
  const { t } = useTranslation();

  if (lockedValue !== null) {
    return (
      <HStack gap={2} align="center">
        <Text
          w={KEY_COLUMN_WIDTH}
          flexShrink={0}
          fontFamily="mono"
          fontSize="xs"
          color="fg.muted"
          truncate
        >
          {utmKey}
        </Text>
        <HStack
          flex={1}
          minW={0}
          gap={1.5}
          h="36px"
          px={2.5}
          align="center"
          bg="bg.muted"
          border="1px solid"
          borderColor="border.muted"
          borderRadius="md"
        >
          <Box color="fg.muted" display="flex" flexShrink={0}>
            <Lock width={13} height={13} />
          </Box>
          <Text flex={1} minW={0} fontFamily="mono" fontSize="xs" color="fg.muted" truncate>
            {lockedValue}
          </Text>
          <Text
            flexShrink={0}
            fontSize="2xs"
            textTransform="uppercase"
            letterSpacing="wide"
            color="fg.subtle"
          >
            {t('mentions.boost.setup.utm.lockedTag')}
          </Text>
        </HStack>
      </HStack>
    );
  }

  const keyErrorText =
    keyError === 'duplicate'
      ? t('mentions.boost.setup.utm.duplicateKeyError')
      : t('mentions.boost.setup.utm.invalidKeyError');

  return (
    <Box>
      {/* The key input carries no label on this row, so it is a bare Input like
          the value one: wrapping it in a Field rendered the (empty) label stack
          above it and pushed it off the value input's line. The error, when
          there is one, hangs under the row instead of growing inside a column
          and shoving its neighbours around. */}
      <HStack gap={2} align="center">
        {keyReadOnly ? (
          <Text
            w={KEY_COLUMN_WIDTH}
            flexShrink={0}
            fontFamily="mono"
            fontSize="xs"
            color="fg.muted"
            truncate
          >
            {utmKey}
          </Text>
        ) : (
          <Input
            w={KEY_COLUMN_WIDTH}
            flexShrink={0}
            size="sm"
            variant="outline"
            fontFamily="mono"
            fontSize="xs"
            value={utmKey}
            aria-invalid={keyError !== null}
            placeholder={t('mentions.boost.setup.utm.keyPlaceholder')}
            onChange={(event) => onKeyChange(index, event.target.value)}
          />
        )}
        <Input
          flex={1}
          minW={0}
          size="sm"
          variant="outline"
          fontFamily="mono"
          fontSize="xs"
          value={value}
          placeholder={t('mentions.boost.setup.utm.valuePlaceholder')}
          onChange={(event) => onValueChange(index, event.target.value)}
        />
        <IconButton
          aria-label={t('mentions.boost.setup.utm.remove')}
          size="xs"
          variant="ghost"
          color="fg.muted"
          flexShrink={0}
          onClick={() => onRemove(index)}
        >
          <Xmark />
        </IconButton>
      </HStack>

      {!keyReadOnly && keyError !== null && (
        <Text mt={1} fontSize="xs" color="fg.error">
          {keyErrorText}
        </Text>
      )}
    </Box>
  );
}
